import React, { useState, useCallback } from 'react';
import style from 'styled-components';
import { Palette } from 'core/config';
import { useWindowSize } from './hooks';
import { CardBody, CardFooter, CardHeader, Button } from 'view/components/common';
import { FormCheckbox } from 'view/components/common/inputs';
import { Spinner } from 'view/components/layout';

interface TermsProps {
    stepNavigator : (increment: boolean) => () => void;
}

export const Terms: React.FunctionComponent<TermsProps> = ({ stepNavigator }) => 
{
    const [ loading, setLoading ] = useState(false);
    const [ hasAgreed, setHasAgreed ] = useState(false);
    const size = useWindowSize();
    const isLargeScreen = size.width >= 1900;

    const openURL = useCallback(url => 
    {
        return () => 
        {
            window.open(url, '_blank');
        };
    }, []);

    const onSubmit = useCallback(() => 
    {
        setLoading(true);
        setTimeout(() => 
        {
            stepNavigator(true)();
        }, 500);
    }, [ stepNavigator, setLoading ]);

    return (
        <>
            <CardHeader
                content={'Mortgage Approval'}
                step={'Step 1'}
                backHandler={stepNavigator(false)}
            />
            {loading ? (
                <Spinner css='height: inherit; margin-top: 175px;' />
            ): (
                <>
                    <CardBody>
                        <>
                            <Row>
                                <Paragraph>
                                    In order to underwrite and approve your application, Manzil will require documentation, banking information, and a full credit check.
                                </Paragraph>
                            </Row>

                            <Row>
                                <Column css={'flex: 0 0 34px; margin-left: -10px; padding-top: 2px;'}>
                                    <FormCheckbox
                                        name={'hasAgreed'}
                                        checked={hasAgreed}
                                        onChange={() => setHasAgreed(v => !v)}
                                    />
                                </Column>
                                <Column css={''}>
                                    <Paragraph>
                                        I have agreed to the
                                        <span onClick={openURL('https://manzil.ca/terms-conditions/')}>
                                            Terms & Conditions
                                        </span>
                                        and the
                                        {isLargeScreen && (
                                            <span onClick={openURL('https://manzil.ca/privacy-policy/')}>
                                                Privacy Policy
                                            </span>
                                        )}
                                    </Paragraph>
                                    {!isLargeScreen && (
                                        <PrivacyPolicy>
                                            <span onClick={openURL('https://manzil.ca/privacy-policy/')}>
                                                Privacy Policy
                                            </span>
                                        </PrivacyPolicy>
                                    )}
                                </Column>
                            </Row>
                        </>
                    </CardBody>
                    <CardFooter>
                        <Button
                            title="Continue Underwriting"
                            type="button"
                            disabled={!(hasAgreed)}
                            onClick={onSubmit}
                            css={'text-transform: none; padding-bottom: 3px;'}
                            arrowCss={'padding-left: 0px; margin-top: 5px;'}
                        />
                    </CardFooter>
                </>
            )}
        </>
    );
};

const Row = style.div`
    display: flex;
    padding-left: 60px;
    padding-right: 60px;

    @media only screen and (min-width: 1900px) {
        padding-right: 40px;
    }
`;

const Column = style.div<{ css: string }>`
    ${props => props.css && props.css}
`;

const Paragraph = style.p`
  font: normal normal normal 10px 'SofiaPro-Regular';
  whitespace:nowrap;
  color: ${Palette.BLUE};
  font-weight: 200;
  line-height: 18px;


  span {
    font: normal normal normal 10px 'SofiaPro-Bold';
    text-decoration: underline;
    cursor: pointer;
    padding-left: 4px;
    padding-right: 4px;
  }

  @media only screen and (min-width: 1300px) {
    margin-top: 10px;
    font-size: 12px;

    span {
      font-size: 12px;
    }

    a {
      font-size: 12px;
    }
  }

  @media only screen and (min-width: 1900px) {
    font-size: 14px;

    span {
      font-size: 14px;
    }

    a {
      font-size: 14px; 
    }
  }
`;

const PrivacyPolicy = style(Paragraph)`
  margin-left: -4px;
  margin-top: -10px;
`;